import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, BarChart3, Cog, GraduationCap, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const needs = [
  { icon: Lightbulb, label: "Create", description: "Write, design, generate content", tools: ["Blog Assistant", "AI Image Generator", "PDF StoryBook"], route: "/tools" },
  { icon: BarChart3, label: "Analyze", description: "Review contracts, data, SEO", tools: ["Contract Analyzer", "SEO Analyzer", "Code Assistant"], route: "/contract-analyzer" },
  { icon: Cog, label: "Automate", description: "Build workflows and chatbots", tools: ["GenieBot Workflow", "Chatbot Builder", "Pipelines"], route: "/chatbot-builder" },
  { icon: GraduationCap, label: "Learn", description: "Study, translate, improve skills", tools: ["Math Professor", "Language Translator", "College Ready"], route: "/tools" },
];

export const FindYourGenieBot = () => {
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <section className="py-20 px-4" aria-labelledby="find-geniebot-heading">
      <div className="max-w-4xl mx-auto text-center">
        <h2 id="find-geniebot-heading" className="text-3xl md:text-4xl font-bold mb-3">Find Your GenieBot</h2>
        <p className="text-muted-foreground mb-10 max-w-lg mx-auto">
          What do you need help with today?
        </p>

        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-4 gap-4 mb-8" role="radiogroup" aria-label="Select your need">
          {needs.map((need, i) => {
            const Icon = need.icon;
            const isActive = selected === i;
            return (
              <motion.button
                key={need.label}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelected(i)}
                role="radio"
                aria-checked={isActive}
                aria-label={`${need.label}: ${need.description}`}
                className={`p-5 sm:p-6 rounded-2xl border-2 transition-all text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none ${
                  isActive
                    ? "border-primary bg-primary/10 shadow-[0_0_24px_hsl(var(--primary)/0.15)]"
                    : "border-border bg-card hover:border-primary/30"
                }`}
              >
                <Icon className={`h-6 w-6 sm:h-7 sm:w-7 mb-3 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                <div className="font-semibold text-base sm:text-lg">{need.label}</div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1">{need.description}</div>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          {selected !== null && (
            <motion.div
              key={selected}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="p-6 rounded-2xl border border-primary/20 bg-card max-w-lg mx-auto"
              role="region"
              aria-live="polite"
              aria-label="Recommended tools"
            >
              <p className="text-sm text-muted-foreground mb-3">Recommended tools for you:</p>
              <div className="flex flex-wrap gap-2 justify-center mb-5">
                {needs[selected].tools.map((tool) => (
                  <span key={tool} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                    {tool}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-center gap-3">
                <Button asChild className="gap-2">
                  <Link to={needs[selected].route}>
                    Get Started <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Reset
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
